import 'swiper/css';
import 'swiper/css/controller';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';

import {
    Box,
    CardMedia,
    Skeleton
} from '@mui/material';
import {
    Controller,
    Pagination,
    Zoom
} from 'swiper';
import {
    Swiper,
    SwiperClass,
    SwiperProps,
    SwiperSlide
} from 'swiper/react';

import { AddPhotoIcon } from '../../icons';
import { EmptyImagePlaceholder } from './EmptyImagePlaceholder';
import ImageCarouselButton from '../../../componentsV2/image/carouselComponent/ImageCarouselButton';
import { ImageSourceOverlay } from '../ImageSourceOverlay';
import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import { Property } from 'csstype';
import { ResolvedImageEntity } from '../../../lib/dashboard/types/ImageEntity';
import { isMobile } from 'react-device-detect';
import { useState } from 'react';

type ImageCarouselProps = Pick<SwiperProps, "onSwiper" | "controller" | "onClick" | "zoom"> & {
    images: Array<ResolvedImageEntity> | undefined;
    swiper: SwiperClass | undefined;
    objectFit?: Property.ObjectFit;
    variant: "thumbnail" | "full";
    showUploadImages?: boolean;
    onUploadImagesClick?: () => void;
};

export const ImageCarousel = (props: ImageCarouselProps) => {
    const { images, swiper, objectFit = "contain", variant, showUploadImages = false, onUploadImagesClick, ...swiperProps } = props;
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const renderNavigation = !isMobile && images && images.length > 1;

    if (!images) {
        return (
            <Box display="flex" width="100%" height="100%">
                <Skeleton variant="rectangular" width="100%" height="100%" />
            </Box>
        );
    }

    return (
        <Box
            display="flex"
            width="100%"
            height="100%"
            sx={{ "--swiper-pagination-color": theme => theme.palette.primary.main }}
        >
            {renderNavigation && (
                <Box
                    flex={1}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    bgcolor="#D9D9D9"
                    sx={{
                        cursor: activeIndex === 0 ? "default" : "pointer",
                    }}
                    onClick={() => swiper?.slidePrev()}
                    borderRadius="8px 0 0 8px"
                >
                    <KeyboardArrowLeftOutlinedIcon
                        color={activeIndex === 0 ? "disabled" : "inherit"}
                        fontSize="small"
                    />
                </Box>
            )}
            <Swiper
                style={{ width: "100%", borderRadius: renderNavigation ? undefined : 8 }}
                modules={[Pagination, Controller, Zoom]}
                pagination={{
                    type: images.length <= 5 ? "bullets" : "progressbar",
                    clickable: true
                }}
                onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                {...swiperProps}
            >
                {images.length > 0 ? images.map((image, index) => {
                    return (
                        <SwiperSlide key={index}>
                            <Box width="100%" height="100%" sx={{ containerType: "inline-size" }} className="swiper-zoom-container">
                                <CardMedia
                                    src={image.urls.original}
                                    srcSet={variant === "thumbnail" && image.urls.thumbnail ? `${image.urls.thumbnail}` : undefined}
                                    alt="Image"
                                    component={"img"}
                                    style={{ height: "100%", width: "100%", objectFit }}
                                    loading="lazy"
                                />
                                <Box className='swiper-lazy-preloader' />
                            </Box>
                            {image.source && (
                                <Box position="absolute" bottom={0} right={0}>
                                    <ImageSourceOverlay source={image.source} />
                                </Box>
                            )}
                        </SwiperSlide>
                    );
                }) : (
                    <SwiperSlide>
                        <EmptyImagePlaceholder />
                    </SwiperSlide>
                )}
                {showUploadImages && (
                    <Box position="absolute" bottom={4} left={4} zIndex={1000}>
                        <ImageCarouselButton
                            onClick={onUploadImagesClick}
                            icon={<AddPhotoIcon />}
                            tooltip="Upload Images"
                        />
                    </Box>
                )}
            </Swiper>
            {renderNavigation && (
                <Box
                    flex={1}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    bgcolor="#D9D9D9"
                    sx={{
                        cursor: images && activeIndex === images.length - 1 ? "default" : "pointer",
                        visibility: images && images.length <= 1 ? "hidden" : "visible"
                    }}
                    onClick={() => swiper?.slideNext()}
                    borderRadius="0 8px 8px 0"
                >
                    <KeyboardArrowRightOutlinedIcon
                        color={images && activeIndex === images.length - 1 ? "disabled" : "inherit"}
                        fontSize="small"
                    />
                </Box>
            )}
        </Box>
    );
};